import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchDividendTransition } from '@apis/dividends';
import type { DividendTransitionApiResult } from '@app-types/dividendApi';
import { getAccessToken } from '@utils/authStorage';
import { getIframeQueryParamsFromSearch } from '@utils/queryParams';

/**
 * React Query hook for the dividend transition data
 * (GET /api/dividends/dividend-transition). `years` (5 or 10) comes from the
 * display-period dropdown and is part of the query key so switching periods
 * does not reuse stale transition data.
 */
export const useDividendTransition = (years: number) => {
  const location = useLocation();
  const { ticker, mkt } = getIframeQueryParamsFromSearch(location.search);
  const hasToken = Boolean(getAccessToken());
  const safeYears = Number.isFinite(years) && years > 0 ? years : 10;

  return useQuery<DividendTransitionApiResult>({
    queryKey: ['dividends', 'dividend-transition', ticker, mkt, safeYears],
    queryFn: () =>
      fetchDividendTransition({
        ticker: ticker as string,
        mkt: mkt as string,
        years: safeYears,
      }),
    enabled: Boolean(ticker && mkt && hasToken),
    staleTime: 60_000,
    retry: 1,
  });
};
